import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';

export interface SpreadsheetColumn<T> {
  header: string;
  value: (row: T) => string | number | null | undefined;
  width?: number;
}

export type SpreadsheetCell = string | number;

export interface TableContent {
  headers: string[];
  rows: SpreadsheetCell[][];
}

const IGNORED_HEADERS = ['ações', 'ação', 'expandir'];
const CURRENCY_PATTERN = /^-?\s*R\$\s*-?\s*(\d{1,3}(\.\d{3})*|\d+)(,\d+)?$/;
const NUMBER_PATTERN = /^-?(\d{1,3}(\.\d{3})+|\d+)(,\d+)?$/;

@Injectable({ providedIn: 'root' })
export class SpreadsheetExportService {
  export<T>(fileName: string, sheetName: string, columns: SpreadsheetColumn<T>[], rows: T[]): void {
    XLSX.writeFile(this.buildWorkbook(sheetName, columns, rows), `${fileName}.xlsx`);
  }

  exportTable(fileName: string, sheetName: string, table: HTMLTableElement): void {
    const { headers, rows } = this.readTable(table);
    const columns = headers.map((header, index) => ({ header, value: (row: SpreadsheetCell[]) => row[index] }));
    this.export(fileName, sheetName, columns, rows);
  }

  readTable(table: HTMLTableElement): TableContent {
    const headerRow = table.tHead?.rows[table.tHead.rows.length - 1];
    if (!headerRow) return { headers: [], rows: [] };

    const headerCells = Array.from(headerRow.cells);
    const bodyRows = [...Array.from(table.tBodies).flatMap(body => Array.from(body.rows)), ...Array.from(table.tFoot?.rows ?? [])]
      .filter(row => !(row.cells.length === 1 && row.cells[0].colSpan > 1));
    const texts = bodyRows.map(row => Array.from(row.cells).map(cell => this.cellText(cell)));

    const keptIndexes = headerCells
      .map((cell, index) => ({ index, header: this.normalize(cell.textContent) }))
      .filter(({ index, header }) => !IGNORED_HEADERS.includes(header.toLowerCase()) && !this.isControlColumn(index, header, texts, bodyRows))
      .map(({ index }) => index);

    return {
      headers: keptIndexes.map(index => this.normalize(headerCells[index].textContent)),
      rows: texts.map(row => keptIndexes.map(index => this.parseCell(row[index] ?? '')))
    };
  }

  parseCell(text: string): SpreadsheetCell {
    if (CURRENCY_PATTERN.test(text)) return this.toNumber(text.replace(/R\$|\s/g, ''));
    if (NUMBER_PATTERN.test(text) && !/^-?0\d/.test(text)) return this.toNumber(text);
    return text;
  }

  buildWorkbook<T>(sheetName: string, columns: SpreadsheetColumn<T>[], rows: T[]): XLSX.WorkBook {
    const headers = columns.map(column => column.header);
    const body = rows.map(row => columns.map(column => column.value(row) ?? ''));
    const sheet = XLSX.utils.aoa_to_sheet([headers, ...body]);
    sheet['!cols'] = columns.map(column => ({ wch: column.width ?? Math.max(column.header.length + 2, 14) }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, sheetName.slice(0, 31));
    return workbook;
  }

  private isControlColumn(index: number, header: string, texts: string[][], bodyRows: HTMLTableRowElement[]): boolean {
    const withoutText = texts.every(row => (row[index] ?? '') === '');
    const withControls = bodyRows.some(row => row.cells[index]?.querySelector('button, a'));
    return withoutText && (header === '' || !!withControls);
  }

  private toNumber(text: string): number {
    return Number(text.replace(/\./g, '').replace(',', '.'));
  }

  private normalize(text: string | null): string {
    return (text ?? '').replace(/\s+/g, ' ').trim();
  }

  private cellText(cell: HTMLTableCellElement): string {
    const clone = cell.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('table, p-table').forEach(nested => nested.remove());
    clone.querySelectorAll<HTMLInputElement>('input[type="checkbox"], input[type="radio"]').forEach(input => {
      input.replaceWith(cell.ownerDocument.createTextNode(input.checked ? 'Sim' : 'Não'));
    });
    clone.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea').forEach(input => {
      input.replaceWith(cell.ownerDocument.createTextNode(input.value));
    });
    clone.querySelectorAll<HTMLSelectElement>('select').forEach(select => {
      select.replaceWith(cell.ownerDocument.createTextNode(select.selectedOptions[0]?.textContent ?? ''));
    });
    return this.normalize(clone.textContent);
  }
}
